import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

export interface ProductSubgroup {
  id: number;
  descricao?: string;
  referencia?: string;
  isInativo: boolean;
}

export interface ProductSubgroupFormData {
  descricao?: string;
  referencia?: string;
  isInativo?: boolean;
}

export function useProductSubgroups() {
  const queryClient = useQueryClient();

  const { data: subgroups = [], isLoading } = useQuery({
    queryKey: ["product-subgroups"],
    queryFn: async () => {
      const { data } = await apiClient.get<ProductSubgroup[]>("/api/subgrupos-produto");
      return data;
    },
  });

  const createSubgroup = useMutation({
    mutationFn: async (formData: ProductSubgroupFormData) => {
      const { data } = await apiClient.post<ProductSubgroup>("/api/subgrupos-produto", formData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-subgroups"] });
      toast.success("Subgrupo criado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao criar subgrupo: " + error.message);
    },
  });

  const updateSubgroup = useMutation({
    mutationFn: async ({ id, ...formData }: ProductSubgroupFormData & { id: number }) => {
      const { data } = await apiClient.put<ProductSubgroup>(`/api/subgrupos-produto/${id}`, formData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-subgroups"] });
      toast.success("Subgrupo atualizado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao atualizar subgrupo: " + error.message);
    },
  });

  const deleteSubgroup = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/api/subgrupos-produto/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-subgroups"] });
      toast.success("Subgrupo inativado com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao inativar subgrupo: " + error.message);
    },
  });

  return {
    subgroups,
    isLoading,
    createSubgroup,
    updateSubgroup,
    deleteSubgroup,
  };
}
